import { useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import type { Location } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthCard, PrimaryAuthButton } from '@/components/AuthCard';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    const from = (location.state as { from?: Location } | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 429 ? 'Demasiados intentos. Espera un minuto.' : err.message);
      } else {
        setError('No se ha podido conectar con el servidor.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      subtitle="Inicia sesión para fichar tu jornada."
      meta={
        <Link
          to="/password/reset"
          className="text-[14px] font-medium text-icaria-600 hover:text-icaria-700"
        >
          ¿Olvidaste tu contraseña?
        </Link>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu.email@icaria-atelier.com"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Contraseña</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••"
          />
        </div>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <PrimaryAuthButton submitting={submitting}>
          {submitting ? 'Entrando…' : 'Entrar'}
        </PrimaryAuthButton>

        <p className="text-[12px] text-muted-foreground text-center">
          <Link to="/legal/privacidad" className="hover:text-foreground underline-offset-2 hover:underline">
            Política de privacidad
          </Link>
        </p>
      </form>
    </AuthCard>
  );
}
